import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API } from '../../utils/apiConfig';
import { errMsg, inr, monthLabel, num } from './posUtils';

const ImpactBox = ({ i }) => (
  <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-xs text-amber-900" data-testid={`impact-${i.month}`}>
    <div className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" /> {monthLabel(i.month)} already has data — it will be replaced</div>
    <div className="grid grid-cols-2 gap-x-4">
      <span>Records: {i.existing_records} → {i.new_records}</span>
      <span>Sales: {inr(i.existing_sales)} → {inr(i.new_sales)}</span>
      <span>Orders: {num(i.existing_orders)} → {num(i.new_orders)}</span>
      <span>Points earned: {num(i.existing_points_earned)} → {num(i.new_points_earned)}</span>
      <span>Customers added: {i.customers_added}</span>
      <span>Customers removed: {i.customers_removed}</span>
      <span>Customers changed: {i.customers_changed}</span>
    </div>
  </div>
);

const SheetRow = ({ s, month, setMonth }) => (
  <div className="border rounded-lg p-3 space-y-2" data-testid={`preview-sheet-${s.sheet}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="font-semibold flex items-center gap-1"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Sheet: {s.sheet}</div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">{s.detected_month ? `Detected from ${s.detection_source}` : 'Month not detected — select it'}</span>
        <Input type="month" value={month || ''} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40" data-testid={`preview-month-${s.sheet}`} />
      </div>
    </div>
    {s.error ? <p className="text-xs text-red-600">{s.error}</p> : (
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <span>Processed <b>{s.processed}</b></span><span className="text-emerald-700">Accepted <b>{s.accepted}</b></span>
        <span className="text-red-600">Rejected <b>{s.rejected}</b></span><span className="text-amber-700">Duplicates <b>{s.duplicates}</b></span>
        <span className="text-amber-700">Warnings <b>{s.warnings}</b></span>
        <span>Sales <b>{inr(s.totals?.sales)}</b></span><span>Orders <b>{num(s.totals?.orders)}</b></span>
        <span>Pts <b>{num(s.totals?.points_earned)}</b>/<b>{num(s.totals?.points_burned)}</b></span>
      </div>
    )}
    {s.issues?.length > 0 && (
      <ul className="text-[11px] text-slate-600 max-h-24 overflow-y-auto bg-slate-50 rounded p-2">
        {s.issues.map((e, k) => <li key={k}>Row {e.row}{e.customer_id ? ` (ID ${e.customer_id})` : ''}: {e.reason}</li>)}
      </ul>
    )}
    {s.impact && month === s.detected_month && <ImpactBox i={s.impact} />}
  </div>
);

export const ImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [months, setMonths] = useState({});
  const [impacts, setImpacts] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setPreview(null); setMonths({}); setImpacts(null); };
  const close = () => { reset(); onClose(); };

  const upload = async () => {
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pos/import/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      setMonths(Object.fromEntries(r.data.sheets.map((s) => [s.sheet, s.error ? '' : (s.detected_month || '')])));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const commit = async (confirm) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pos/import/commit`, { token: preview.token, months, confirm_replace: confirm });
      toast.success(`Imported ${r.data.accepted} customer records (${r.data.months.map(monthLabel).join(', ')})`);
      onDone(); close();
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.impacts) setImpacts(e.response.data.impacts);
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pos-import-dialog">
        <DialogHeader><DialogTitle>Import POS Customer Sales</DialogTitle></DialogHeader>
        {!preview && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Upload a .xlsx or .xls file. Each sheet is treated as one month. Required columns: Customer ID and Sales.</p>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </div>
        )}
        {preview && !impacts && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600"><b>{preview.filename}</b> — confirm the month for each sheet. Leave a month empty to skip that sheet.</p>
            {preview.already_imported && (
              <p className="text-xs text-amber-700" data-testid="already-imported-note">This exact file was already imported on {new Date(preview.already_imported.created_at).toLocaleString('en-IN')}.</p>
            )}
            {preview.sheets.map((s) => <SheetRow key={s.sheet} s={s} month={months[s.sheet]} setMonth={(v) => setMonths((m) => ({ ...m, [s.sheet]: v }))} />)}
          </div>
        )}
        {impacts && (
          <div className="space-y-2" data-testid="replace-confirm">
            <p className="text-sm font-semibold text-slate-800">Existing data will be replaced. Review the impact before confirming:</p>
            {impacts.map((i) => <ImpactBox key={i.month} i={i} />)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={impacts ? () => setImpacts(null) : close} data-testid="import-cancel-btn">{impacts ? 'Back' : 'Cancel'}</Button>
          {!preview && <Button disabled={!file || busy} onClick={upload} data-testid="import-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Validate File</Button>}
          {preview && !impacts && <Button disabled={busy} onClick={() => commit(false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-commit-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Import</Button>}
          {impacts && <Button disabled={busy} onClick={() => commit(true)} className="bg-red-600 hover:bg-red-700" data-testid="import-confirm-replace-btn">Confirm Replace</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
